/**
 * useIbkrLiveStatus — reusable live IBKR connection-status hook.
 *
 * Owns the live-status state shared by the Dashboard indicator and the Settings
 * panel. Runs a check on mount and when the browser tab regains focus/visibility,
 * de-dupes concurrent checks, debounces focus storms, and exposes a manual
 * refresh. Implements the design's `runCheck` algorithm.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.6, 3.7, 3.8, 3.9, 3.10, 3.11,
 * 3.12, 3.13; design Properties 4 (no overlapping checks), 5 (monotonic
 * checkedAt on success), 6 (focus debounce).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectionStatus } from '@covered-calls/shared';
import type { IbkrStatusReason } from '@covered-calls/shared';
import { getIbkrLiveStatus } from '../services/ibkr-settings-service';

/**
 * Render-time UI status state. Extends the persisted {@link ConnectionStatus}
 * enum with two UI-only states that are never persisted or returned by the
 * backend.
 */
export type LiveStatusUiState = ConnectionStatus | 'CHECKING' | 'UNKNOWN';

/** Result surface exposed by {@link useIbkrLiveStatus}. */
export interface UseIbkrLiveStatusResult {
  /** Current UI status. `CHECKING` while a check is in flight. */
  status: LiveStatusUiState;
  /** ISO timestamp of the last successful check, or null if none succeeded. */
  checkedAt: string | null;
  /** True while a check is in flight. */
  isChecking: boolean;
  /** Human-readable reason for the most recent failed check, if any. */
  error: string | null;
  /**
   * Human-readable status message from the backend for the latest check
   * (e.g. why the connection is down). Null when connected or unavailable.
   */
  message: string | null;
  /** Machine-readable cause for a non-connected status, if provided. */
  reason: IbkrStatusReason | null;
  /** Manual trigger; obeys the in-flight and interval guards. */
  refresh: () => void;
}

/**
 * Minimum interval between consecutive network checks. Debounces focus/visibility
 * storms so returning to the tab repeatedly does not fan out into a burst of
 * Lambda invocations (design Property 6).
 */
export const MIN_CHECK_INTERVAL_MS = 3000;

/** Derives a human-readable message from an unknown thrown value. */
function humanMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Failed to check IBKR connection status.';
}

export function useIbkrLiveStatus(): UseIbkrLiveStatusResult {
  const [status, setStatus] = useState<LiveStatusUiState>('UNKNOWN');
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reason, setReason] = useState<IbkrStatusReason | null>(null);

  // Refs to avoid stale closures inside event listeners and async callbacks.
  const isCheckingRef = useRef<boolean>(false);
  const lastCompletedAtRef = useRef<number | null>(null);
  const checkedAtRef = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);

  const runCheck = useCallback(async (): Promise<void> => {
    // De-dupe: at most one check in flight (Property 4).
    if (isCheckingRef.current) {
      return;
    }

    // Debounce: skip if the last completed check was too recent (Property 6).
    const last = lastCompletedAtRef.current;
    if (last !== null && Date.now() - last < MIN_CHECK_INTERVAL_MS) {
      return;
    }

    isCheckingRef.current = true;
    if (mountedRef.current) {
      setIsChecking(true);
      setStatus('CHECKING');
    }

    try {
      const result = await getIbkrLiveStatus();
      // Ignore results that resolve after unmount (abort semantics).
      if (!mountedRef.current) {
        return;
      }
      checkedAtRef.current = result.checkedAt;
      setStatus(result.status);
      setCheckedAt(result.checkedAt);
      setError(null);
      // The backend returns a human-readable message (and machine-readable
      // reason) explaining a non-connected status on a successful 200 response.
      // Surface them so the UI can show WHY the connection is down.
      setMessage(result.message ?? null);
      setReason(result.reason ?? null);
    } catch (e: unknown) {
      if (!mountedRef.current) {
        return;
      }
      // Retain the last known checkedAt; never regress it to null (Property 5).
      setStatus(checkedAtRef.current === null ? 'UNKNOWN' : ConnectionStatus.DISCONNECTED);
      const msg = humanMessage(e);
      setError(msg);
      setMessage(msg);
      setReason(null);
    } finally {
      isCheckingRef.current = false;
      lastCompletedAtRef.current = Date.now();
      if (mountedRef.current) {
        setIsChecking(false);
      }
    }
  }, []);

  const refresh = useCallback((): void => {
    void runCheck();
  }, [runCheck]);

  useEffect(() => {
    mountedRef.current = true;

    // One check on mount.
    void runCheck();

    const handleFocus = (): void => {
      void runCheck();
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void runCheck();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup: remove listeners and ignore any in-flight result post-unmount.
      mountedRef.current = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [runCheck]);

  return { status, checkedAt, isChecking, error, message, reason, refresh };
}
